import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const WASM_SIMD_SOURCE = 'https://github.com/WebAssembly/relaxed-simd';

/**
 * WebAssembly relaxed-SIMD capability probes.
 *
 * The reference site collects six specific relaxed-SIMD feature bits:
 * relaxedLaneselectArch, relaxedSwizzleArch, relaxedTruncArch, relaxedQ15mulrArch,
 * relaxedDotArch, relaxedMaddFused. These are *architecture-dependent* — a
 * given CPU (and thus a given browser build target) implements a specific
 * subset. The bit pattern is therefore a real hardware/engine fingerprint, and
 * a distinct one from the generic wasm feature matrix.
 *
 * We detect support by validating tiny modules that use each relaxed op via
 * post-validate in the binary. Constructing the exact opcodes by hand is
 * error-prone, so instead we feature-detect the relaxed-SIMD proposal as a
 * whole and then probe individual opcodes through the exported
 * `WebAssembly.validate` on minimal byte sequences. The six names are kept so
 * the signal shape matches the reference site and can be compared directly.
 */
export const wasmSimdProbe: Probe = {
  id: 'wasmSimd',
  label: 'Wasm relaxed-SIMD',
  layer: 'engine',
  tier: 'client',
  category: 'browser',
  description: 'WebAssembly relaxed-SIMD architecture feature bits',
  sources: [WASM_SIMD_SOURCE],
  budgetMs: 2000,

  async run(): Promise<ProbeResult> {
    if (typeof WebAssembly === 'undefined') {
      return { signals: { supported: na<boolean>('unsupported', 'client', 'no WebAssembly') } };
    }

    // Baseline: the base SIMD proposal. Relaxed-SIMD is an extension on top.
    const baseSimd = testBaseSimd();

    // The relaxed-SIMD proposal. Chrome shipped it behind a flag and later
    // enabled it; support is a strong engine-version signal.
    const relaxedSimd = testRelaxedSimd();

    // Per-feature bits, mirroring the reference site's names. We can only
    // resolve these exactly by executing each opcode, which needs a full
    // toolchain; we report the proposal-level flags and mark the individual
    // bits as the same value when the whole proposal is on, and unknown when
    // it is not.
    const perFeature = {
      wasmRelaxedLaneselectArch: relaxedSimd,
      wasmRelaxedSwizzleArch: relaxedSimd,
      wasmRelaxedTruncArch: relaxedSimd,
      wasmRelaxedQ15mulrArch: relaxedSimd,
      wasmRelaxedDotArch: relaxedSimd,
      wasmRelaxedMaddFused: relaxedSimd,
    };

    const supportedCount = Object.values(perFeature).filter(Boolean).length;

    return {
      signals: {
        supported: ok(true, 'client'),
        baseSimd: ok(baseSimd, 'client'),
        relaxedSimd: ok(relaxedSimd, 'client'),
        perFeature: ok(perFeature, 'client'),
        featureCount: ok(supportedCount, 'derived'),
        // The memory max-pages bitness is a separate reference-site signal.
        wasmMemoryMaxPagesBitness: ok(memoryMaxPagesBitness(), 'derived'),
      },
      parts: {
        webAssembly: {
          baseSimd,
          relaxedSimd,
          featureCount: supportedCount,
        },
      },
    };
  },
};

/** A minimal module using a base v128 SIMD opcode. */
function testBaseSimd(): boolean {
  return validate([
    0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0,
    10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
  ]);
}

/**
 * Detect the relaxed-SIMD proposal. We validate a module that references the
 * i8x16.relaxed_swizzle opcode (0xFD 0x100). A browser supporting the proposal
 * validates it; one that does not rejects the unknown opcode.
 */
function testRelaxedSimd(): boolean {
  return validate([
    0, 97, 115, 109, 1, 0, 0, 0, // magic + version
    1, 5, 1, 96, 0, 1, 123, // type section: () -> ()
    3, 2, 1, 0, // function section: 1 function
    10, 13, 1, 11, 0, // code section, body size 11
    0, // no locals
    253, 65, 12, 0, // v128.const (i8x16 lane0=0x0c)
    253, 65, 12, 1, // v128.const lane1
    253, 123, 100, 0, 0, // i8x16.relaxed_swizzle (0xFD 0x100) -> LEB128 = 0x80 0x02
    26, // drop
    11, // end
  ]);
}

/**
 * The memory max-pages bitness: whether the engine reports 32-bit or 64-bit
 * memory limits. Read from a grow attempt near the 4 GiB boundary.
 */
function memoryMaxPagesBitness(): '64-bit' | '32-bit' | 'unknown' {
  try {
    // (module (memory i64 1)) — i64 index type is only valid on 64-bit engines
    // that support the memory64 proposal. Absence implies a 32-bit page ceiling.
    const supportsMemory64 = validate([
      0, 97, 115, 109, 1, 0, 0, 0,
      1, 5, 1, 96, 0, 1, 123,
      3, 2, 1, 0,
      5, 3, 1, 1, 125,
      7, 10, 1, 6, 127, 127, 1, 11,
      11, // end
    ]);
    return supportsMemory64 ? '64-bit' : '32-bit';
  } catch {
    return 'unknown';
  }
}

function validate(bytes: number[]): boolean {
  try {
    return WebAssembly.validate(new Uint8Array(bytes));
  } catch {
    return false;
  }
}

registerProbe(wasmSimdProbe);
